import QRCode from 'qrcode'

/** Colori 8-hex (con alpha) — Safari/WebKit è più stretto su formati colore incompleti. */
const QR_COLOR = {
  dark: '#00ff41ff',
  light: '#000000ff',
} as const

const QR_BASE = {
  margin: 2,
  color: QR_COLOR,
  errorCorrectionLevel: 'M' as const,
}

function normalizeQrPayload(text: string): string {
  const payload = String(text ?? '').trim()
  if (!payload) throw new Error('qr_empty_payload')
  // Limite pratico sotto capacity QR v40 (binary ~2953); evita crash encoder.
  if (payload.length > 2500) throw new Error('qr_payload_too_long')
  return payload
}

function logQrError(stage: string, err: unknown): void {
  const msg = err instanceof Error ? `${err.name}: ${err.message}` : String(err)
  console.error(`[ZION QR] ${stage} — ${msg}`, err)
}

async function qrToSvgDataUrl(payload: string, size: number): Promise<string> {
  const svg = await QRCode.toString(payload, {
    ...QR_BASE,
    type: 'svg',
    width: size,
  })
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
}

/**
 * Genera data-URL del QR (PNG via canvas, fallback SVG su WebKit).
 * Mai lasciare eccezioni non gestite al chiamante senza log.
 */
export async function renderQrDataUrl(text: string, size = 260): Promise<string> {
  const payload = normalizeQrPayload(text)
  try {
    return await QRCode.toDataURL(payload, {
      ...QR_BASE,
      width: size,
    })
  } catch (err) {
    logQrError('toDataURL', err)
    try {
      return await qrToSvgDataUrl(payload, size)
    } catch (err2) {
      logQrError('toString/svg fallback', err2)
      throw err2 instanceof Error ? err2 : new Error(String(err2))
    }
  }
}

/**
 * Disegna QR su canvas esistente. Se canvas/context non disponibili (iOS edge),
 * fallback: data-URL → Image → drawImage.
 */
export async function renderQrToCanvas(
  canvas: HTMLCanvasElement,
  text: string,
  size = 260,
): Promise<void> {
  const payload = normalizeQrPayload(text)
  const ctx = canvas.getContext?.('2d')
  if (!ctx) {
    logQrError('getContext', new Error('canvas_2d_unavailable'))
    throw new Error('canvas_2d_unavailable')
  }

  try {
    canvas.width = size
    canvas.height = size
    await QRCode.toCanvas(canvas, payload, {
      ...QR_BASE,
      width: size,
    })
    return
  } catch (err) {
    logQrError('toCanvas', err)
  }

  try {
    const dataUrl = await renderQrDataUrl(payload, size)
    await new Promise<void>((resolve, reject) => {
      const img = new Image()
      img.onload = () => {
        try {
          canvas.width = size
          canvas.height = size
          ctx.clearRect(0, 0, size, size)
          ctx.drawImage(img, 0, 0, size, size)
          resolve()
        } catch (drawErr) {
          reject(drawErr)
        }
      }
      img.onerror = () => reject(new Error('qr_image_load_failed'))
      img.src = dataUrl
    })
  } catch (err2) {
    logQrError('canvas image fallback', err2)
    throw err2 instanceof Error ? err2 : new Error(String(err2))
  }
}
